import { useEffect, useState } from "react";
import { DollarSign, Receipt, Users, Package, Boxes, FileWarning, RotateCcw, AlertTriangle, Coins, TrendingUp, TrendingDown, PackageX } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#3b82f6", "#a855f7", "#ef4444"];

export default function Dashboard() {
  const [period, setPeriod] = useState("week");
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/dashboard/summary?period=${period}`).then(({ data }) => setD(data)).catch(() => toast.error("Failed to load dashboard")); }, [period]);
  if (!d) return <div className="p-6 text-zinc-400">Loading…</div>;

  const money = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const financials = [
    { key: "sales", label: "Sales", value: money(d.sales_total), icon: DollarSign, tone: "" },
    { key: "cost", label: "Cost", value: money(d.cost_total ?? 0), icon: Coins, tone: "" },
    { key: "profit", label: "Profit & Loss", value: money(d.profit ?? 0), icon: (d.profit ?? 0) < 0 ? TrendingDown : TrendingUp, tone: (d.profit ?? 0) < 0 ? "text-red-600" : "text-emerald-600" },
  ];

  const stats = [
    { label: "Orders", value: d.order_count, icon: Receipt },
    { label: "Customers", value: d.total_customers, icon: Users },
    { label: "Products", value: d.total_products, icon: Package },
    { label: "Inventory value", value: `$${d.inventory_value.toLocaleString()}`, icon: Boxes },
  ];
  const secondary = [
    { label: "Unpaid invoices", value: `$${d.unpaid_invoices.toLocaleString()}`, icon: FileWarning },
    { label: "Refunds", value: `$${d.refunds.toLocaleString()}`, icon: RotateCcw },
    { label: "Low stock", value: d.low_stock_count, icon: AlertTriangle },
    { label: "Out of stock", value: d.out_of_stock_count ?? 0, icon: PackageX },
  ];

  return (
    <div className="h-full overflow-y-auto p-6">
      <PageHeader title="Dashboard" subtitle="Truck-parts business at a glance">
        <div className="flex gap-1 p-1 bg-white border border-zinc-200 rounded-xl">
          {["day", "week", "month", "year"].map((r) => (
            <button key={r} data-testid={`period-${r}`} onClick={() => setPeriod(r)} className={`h-9 px-4 rounded-lg text-sm font-semibold capitalize transition ${period === r ? "bg-brand text-white" : "text-zinc-500"}`}>{r}</button>
          ))}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="dash-financials">
        {financials.map((m) => (
          <div key={m.key} data-testid={`fin-${m.key}`} className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400">{m.label}</span><m.icon size={16} className="text-zinc-300" /></div>
            <p className={`font-heading text-2xl font-extrabold mt-2 ${m.tone}`} data-testid={`fin-${m.key}-value`}>{m.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((m) => (
          <div key={m.label} data-testid={`metric-${m.label}`} className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400">{m.label}</span><m.icon size={16} className="text-zinc-300" /></div>
            <p className="font-heading text-2xl font-extrabold mt-2">{m.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {secondary.map((m) => (
          <div key={m.label} data-testid={`metric-${m.label}`} className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="flex items-center justify-between"><span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400">{m.label}</span><m.icon size={16} className="text-zinc-300" /></div>
            <p className="font-heading text-2xl font-extrabold mt-2">{m.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-6" data-testid="out-of-stock-card">
          <h3 className="font-heading text-lg font-bold mb-1">Out of stock <span className="text-sm font-semibold text-red-600">({d.out_of_stock_count ?? 0})</span></h3>
          <p className="text-xs text-zinc-400 mb-3">Products with zero quantity</p>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-zinc-400"><th className="px-2 py-1">Product</th><th className="px-2 py-1">Part #</th><th className="px-2 py-1 text-right">Qty</th></tr></thead>
              <tbody>
                {(d.out_of_stock || []).map((p, i) => (<tr key={i} data-testid={`oos-row-${i}`} className="border-t border-zinc-50"><td className="px-2 py-1.5 font-medium truncate max-w-[160px]">{p.name}</td><td className="px-2 py-1.5 font-mono text-xs text-zinc-500">{p.part_number || "—"}</td><td className="px-2 py-1.5 text-right font-semibold text-red-600">{p.stock}</td></tr>))}
                {(d.out_of_stock || []).length === 0 && <tr><td colSpan={3} className="px-2 py-6 text-center text-zinc-400">Everything in stock</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-6" data-testid="low-stock-card">
          <h3 className="font-heading text-lg font-bold mb-1">Low stock <span className="text-sm font-semibold text-amber-600">({d.low_stock_count ?? 0})</span></h3>
          <p className="text-xs text-zinc-400 mb-3">At or below reorder threshold</p>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm"><thead><tr className="text-left text-[11px] uppercase text-zinc-400"><th className="px-2 py-1">Product</th><th className="px-2 py-1">Part #</th><th className="px-2 py-1 text-right">Qty</th><th className="px-2 py-1 text-right">Threshold</th></tr></thead>
              <tbody>
                {(d.low_stock || []).map((p, i) => (<tr key={i} data-testid={`low-row-${i}`} className="border-t border-zinc-50"><td className="px-2 py-1.5 font-medium truncate max-w-[160px]">{p.name}</td><td className="px-2 py-1.5 font-mono text-xs text-zinc-500">{p.part_number || "—"}</td><td className="px-2 py-1.5 text-right font-semibold text-amber-600">{p.stock}</td><td className="px-2 py-1.5 text-right text-zinc-500">{p.threshold}</td></tr>))}
                {(d.low_stock || []).length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-zinc-400">No low-stock items</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200/70 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Sales trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.sales_by_day}><CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" /><XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={(v) => v.slice(5)} /><YAxis tick={{ fontSize: 12 }} /><Tooltip /><Line type="monotone" dataKey="sales" stroke="#4f46e5" strokeWidth={2.5} dot={{ r: 3 }} /></LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Payment mix</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart><Pie data={d.payment_breakdown} dataKey="amount" nameKey="method" cx="50%" cy="50%" outerRadius={80} labelLine={false} label={(e) => `${(e.percent * 100).toFixed(0)}%`}>{d.payment_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /><Legend /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Top parts</h3>
          <div className="space-y-2.5">
            {d.top_products.map((b, i) => (<div key={i} className="flex items-center gap-3"><span className="font-heading font-bold text-zinc-300 w-4">{i + 1}</span><span className="flex-1 text-sm font-semibold truncate">{b.name}</span><span className="text-sm text-zinc-400">{b.quantity}</span></div>))}
            {d.top_products.length === 0 && <p className="text-zinc-400 text-sm">No sales yet.</p>}
          </div>
        </div>
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Top brands</h3>
          <ResponsiveContainer width="100%" height={200}><BarChart data={d.top_brands}><CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 12 }} /><Tooltip /><Bar dataKey="sold" fill="#4f46e5" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Top categories</h3>
          <ResponsiveContainer width="100%" height={200}><BarChart data={d.top_categories}><CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 12 }} /><Tooltip /><Bar dataKey="sold" fill="#10b981" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
